// src/hooks/useClipboard.js
import { useState, useCallback } from "react";

/**
 * Custom hook to copy text to the clipboard and track the copy status.
 * @returns {{ handleCopy: (textToCopy: string) => void, copyStatus: 'idle' | 'success' | 'error' }}
 */
export const useClipboard = () => {
  const [copyStatus, setCopyStatus] = useState("idle"); // 'idle', 'success', 'error'

  const handleCopy = useCallback(async (textToCopy) => {
    if (typeof textToCopy !== "string") {
      setCopyStatus("error");
      console.error("Attempted to copy non-string data.");
      return;
    }

    const resetStatus = () => setTimeout(() => setCopyStatus("idle"), 2000);

    // --- Modern Clipboard API (Preferred) ---
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopyStatus("success");
        resetStatus();
        return;
      } catch (err) {
        console.warn("Clipboard API failed, falling back to execCommand.", err);
        // Fall through to the fallback
      }
    }

    // --- Fallback Method (document.execCommand) ---
    try {
      const textarea = document.createElement("textarea");
      textarea.value = textToCopy;
      // Position off-screen to avoid visual disruption
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);

      textarea.focus();
      textarea.select();

      const successful = document.execCommand("copy");

      document.body.removeChild(textarea); // Clean up

      if (successful) {
        setCopyStatus("success");
      } else {
        throw new Error("Copy failed via execCommand.");
      }
    } catch (err) {
      setCopyStatus("error");
      console.error("Failed to copy URL using fallback method.", err);
    }

    resetStatus();
  }, []);

  return { handleCopy, copyStatus };
};
